import React, { useState } from "react";
import { Plus, Search, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Country,
  CreateCountryRequest,
  UpdateCountryRequest,
} from "../api/services/countries/type";
import { useCountryManagement } from "../hooks/useCountries";
import { CountryForm } from "../components/countries/CountryForm";
import { CountryTable } from "../components/countries/CountryTable";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { Pagination } from "../components/common/Pagination";
import toast from "react-hot-toast";

export const Countries: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingCountry, setEditingCountry] = useState<Country | undefined>(
    undefined,
  );
  const itemsPerPage = 25;

  const {
    countries,
    isLoading,
    isError,
    error,
    createCountry,
    updateCountry,
    deleteCountry,
    isCreating,
    isUpdating,
    refreshCountries,
  } = useCountryManagement({
    limit: 100,
  });

  const filteredCountries = countries.filter((country) => {
    if (!searchTerm) return true;
    return (
      country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.callingCode?.toString().includes(searchTerm)
    );
  });

  const totalPages = Math.ceil(filteredCountries.length / itemsPerPage);
  const paginatedCountries = filteredCountries.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleCreateCountry = () => {
    setEditingCountry(undefined);
    setShowForm(true);
  };

  const handleEditCountry = (country: Country) => {
    setEditingCountry(country);
    setShowForm(true);
  };

  const handleViewCountry = (country: Country) => {
    navigate(`/countries/${country.id}`);
  };

  const handleFormSubmit = (
    data: CreateCountryRequest | UpdateCountryRequest,
  ) => {
    if (editingCountry) {
      updateCountry(
        { countryId: editingCountry.id, data },
        {
          onSuccess: () => {
            toast.success("Country updated successfully!");
            setShowForm(false);
            setEditingCountry(undefined);
          },
          onError: (error) => {
            toast.error(`Failed to update country: ${error.message}`);
          },
        },
      );
    } else {
      createCountry(data as CreateCountryRequest, {
        onSuccess: () => {
          toast.success("Country created successfully!");
          setShowForm(false);
        },
        onError: (error) => {
          toast.error(`Failed to create country: ${error.message}`);
        },
      });
    }
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [countryToDelete, setCountryToDelete] = useState<string | null>(null);

  const handleDeleteCountry = (countryId: string) => {
    setCountryToDelete(countryId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (countryToDelete) {
      deleteCountry(countryToDelete, {
        onSuccess: () => {
          toast.success("Country deleted successfully!");
          setDeleteDialogOpen(false);
          setCountryToDelete(null);
        },
        onError: (error) => {
          toast.error(`Failed to delete country: ${error.message}`);
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setCountryToDelete(null);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingCountry(undefined);
  };

  if (isError) {
    return (
      <div className="min-h-screen bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-800/20 border border-red-600 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-red-400 mb-2">
              Error Loading Countries
            </h3>
            <p className="text-red-300">
              {error?.message || "Failed to load countries. Please try again."}
            </p>
            <button
              onClick={refreshCountries}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Search and Add Button */}
        <div className="bg-slate-800 rounded-lg p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search countries by name or calling code..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleCreateCountry}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium whitespace-nowrap"
            >
              <Plus size={20} />
              Add Country
            </button>
          </div>
        </div>

        {/* Countries Table */}
        <div className="mb-6">
          <CountryTable
            countries={paginatedCountries}
            onEdit={handleEditCountry}
            onDelete={handleDeleteCountry}
            onView={handleViewCountry}
            isLoading={isLoading}
          />
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}

        {/* Country Form Modal */}
        {showForm && (
          <CountryForm
            country={editingCountry}
            isLoading={isCreating || isUpdating}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          title="Delete Country"
          description="Are you sure you want to delete this country? This action cannot be undone."
          icon={<Trash2 className="h-8 w-8 text-white" />}
          iconBgColor="bg-red-600"
          confirmColor="bg-red-600 hover:bg-red-700"
          confirmText="Delete"
          cancelText="Cancel"
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
      </div>
    </div>
  );
};
